import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, apiUrls } from '@/lib/apiClient';
import { sortCustomerActivities } from '@/lib/activities';
import type { ActivitiesInternal } from '@/types/model.types';
import type { CustomerActivityListSortAndFilter } from '@/types/fund.types';

const YYYY_MM_DD_LENGTH = 10;

/**
 * Replaces DataService.fetchCustomerActivities plus the four BehaviorSubjects
 * (lastTradeTime$, dateQueried$, lastUpdatedTimestamp$, currentTimestamp$).
 * They all came from this one response, so they are just fields on the result
 * now and any component can read them without a subscription.
 *
 * Only the date and tenant go to the server; sorting stays client-side, so
 * changing a sort re-sorts the cached response instead of refetching.
 */
export function useActivities(filter: CustomerActivityListSortAndFilter) {
  const activityDate =
    filter.tradeDateAsYyyyMmDd?.length === YYYY_MM_DD_LENGTH
      ? filter.tradeDateAsYyyyMmDd
      : undefined;

  const query = useQuery({
    queryKey: ['activities', { activityDate, tenant: filter.tenant }],
    queryFn: () =>
      api.get<ActivitiesInternal>(apiUrls.activity, {
        operation: 'retrieving activity',
        params: { activityDate, tenant: filter.tenant },
      }),
  });

  const { sortColumn, sortDirection } = filter;
  const activities = useMemo(
    () =>
      query.data ? sortCustomerActivities(sortColumn, sortDirection, query.data.activities) : [],
    [query.data, sortColumn, sortDirection],
  );

  return {
    ...query,
    activities,
    dateQueried: query.data?.dateQueried ?? '',
    lastTradeTime: query.data?.lastTradeTime ?? null,
    lastUpdatedTimestamp: query.data?.lastUpdated ? new Date(query.data.lastUpdated) : null,
  };
}
